import {
  TagDelimiters,
  TAG_REGEX_PATTERN,
} from 'components/flowBuilder/constants/tags';
import {
  isSendMessageAction,
  isTextToBuyAction,
} from 'components/flowBuilder/types/actions/typeGuards';
import type { Tag } from 'components/flowBuilder/types/tags';
import { css } from 'styled-components';
import { DraftFlow } from '../types';

export const getMergeTagsByCategory = (
  mergeTags: Tag[],
): Record<string, Tag[]> => {
  return mergeTags.reduce<Record<string, Tag[]>>(
    (accMergeTagsByCategory, mergeTag) => {
      const { category } = mergeTag;
      const categoryMergeTags = accMergeTagsByCategory[category] ?? [];
      const newCategoryMergeTags = [...categoryMergeTags, mergeTag];

      return {
        ...accMergeTagsByCategory,
        [category]: newCategoryMergeTags,
      };
    },
    {},
  );
};

export const getSubscriberPropertyMergeTagKey = (property: string): string => {
  return `${TagDelimiters.START}subscriber.custom_properties.get('${property}')${TagDelimiters.END}`;
};

export const getTriggerEventPropertyMergeTagKey = (
  property: string,
): string => {
  // Note: It's important to refer to the event as `initial_event` here, as that's what the BE
  // calls the trigger event.
  return `${TagDelimiters.START}initial_event.get('${property}')${TagDelimiters.END}`;
};

export const removeInvalidTriggerEventPropertyMergeTags = (
  content: string,
  validTriggerEventProperties: string[],
): string => {
  const keyPattern = `${TagDelimiters.START}initial_event\\.get\\((?:"|')((?:\\w|\\s|-)*)(?:"|')\\)${TagDelimiters.END}`;
  const regex = new RegExp(keyPattern, 'g');

  return content
    .replaceAll(regex, (match, propertyName) => {
      const isPropertyValid =
        validTriggerEventProperties.includes(propertyName);

      return isPropertyValid ? match : '';
    })
    .trim();
};

export const convertLegacyMergeTagsInString = (text: string): string => {
  const LEGACY_SUBSCRIBER_MERGE_TAG_REGEX = new RegExp(
    `${TagDelimiters.START}subscriber\\.custom_properties\\.((?:\\w|-)*)${TagDelimiters.END}`,
    'g',
  );

  return text.replaceAll(
    LEGACY_SUBSCRIBER_MERGE_TAG_REGEX,
    (_, propertyName) => {
      return getSubscriberPropertyMergeTagKey(propertyName);
    },
  );
};

export const convertLegacyMergeTags = (flow: DraftFlow): void => {
  flow.actions.forEach((action) => {
    if (isSendMessageAction(action) || isTextToBuyAction(action)) {
      // eslint-disable-next-line no-param-reassign
      action.params.content = convertLegacyMergeTagsInString(
        action.params.content,
      );
    }
  });
};

export const mergeTagBadgeDirectStyles = css`
  background-color: var(--flow-builder-badge-bkg);
  border-radius: calc((1rem + var(--spacing-2)) / 2);
  color: var(--flow-builder-badge-text);
  font-size: var(--body-text-font-size-x-small);
  line-height: 1;
  padding: 2px 1px;
  position: relative;
  top: -1px;
  user-select: all;
  white-space: pre;
`;

export const mergeTagBadgeStyles = (className: string) => css`
  .${className} {
    ${mergeTagBadgeDirectStyles}
  }
`;

export const isMergeTagKey = (value: string): boolean => {
  const tagRegex = new RegExp(`^${TAG_REGEX_PATTERN}$`);
  return tagRegex.test(value);
};

const LinkMergeTagPrefixes = {
  shopLink: 'shop.link',
  abandonedCartLink: 'func.abandoned_cart_link',
  firstProductViewedLink: 'func.first_product_url_viewed',
  lastProductViewedLink: 'func.last_product_url_viewed',
  productLink: 'func.product_url',
  checkoutLink: 'func.product_checkout_url',
  orderStatusUrl: 'func.order_status_url',
  shipmentTrackingLink: 'func.shipment_tracking_link',
} as const;

export const LinkMergeTags = {
  shopLink: {
    key: `${TagDelimiters.START}${LinkMergeTagPrefixes.shopLink}${TagDelimiters.END}`,
    label: 'Shop Link',
    title: 'Shop - Link',
    category: 'Basic info',
    prefix: LinkMergeTagPrefixes.shopLink,
  },
  abandonedCartLink: {
    key: `${TagDelimiters.START}${LinkMergeTagPrefixes.abandonedCartLink}(initial_event)${TagDelimiters.END}`,
    label: 'Abandoned Cart Link',
    title: 'Event - Abandoned Cart Link',
    category: 'Event',
    prefix: LinkMergeTagPrefixes.abandonedCartLink,
  },
  firstProductViewedLink: {
    key: `${TagDelimiters.START}${LinkMergeTagPrefixes.firstProductViewedLink}(initial_event)${TagDelimiters.END}`,
    label: 'First Product Viewed Link',
    title: 'Event - First Product Viewed Link',
    category: 'Event',
    prefix: LinkMergeTagPrefixes.firstProductViewedLink,
  },
  lastProductViewedLink: {
    key: `${TagDelimiters.START}${LinkMergeTagPrefixes.lastProductViewedLink}(initial_event)${TagDelimiters.END}`,
    label: 'Last Product Viewed Link',
    title: 'Event - Last Product Viewed Link',
    category: 'Event',
    prefix: LinkMergeTagPrefixes.lastProductViewedLink,
  },
  productLink: {
    key: `${TagDelimiters.START}${LinkMergeTagPrefixes.productLink}(initial_event)${TagDelimiters.END}`,
    label: 'Product Link',
    title: 'Event - Product Link',
    category: 'Event',
    prefix: LinkMergeTagPrefixes.productLink,
  },
  checkoutLink: {
    key: `${TagDelimiters.START}${LinkMergeTagPrefixes.checkoutLink}(initial_event)${TagDelimiters.END}`,
    label: 'Checkout Link',
    title: 'Event - Checkout Link',
    category: 'Event',
    prefix: LinkMergeTagPrefixes.checkoutLink,
  },
  orderStatusUrl: {
    key: `${TagDelimiters.START}${LinkMergeTagPrefixes.orderStatusUrl}(initial_event)${TagDelimiters.END}`,
    label: 'Order Status URL',
    title: 'Event - Order Status URL',
    category: 'Event',
    prefix: LinkMergeTagPrefixes.orderStatusUrl,
  },
  shipmentTrackingLink: {
    key: `${TagDelimiters.START}${LinkMergeTagPrefixes.shipmentTrackingLink}(initial_event)${TagDelimiters.END}`,
    label: 'Shipment Tracking Link',
    title: 'Event - Shipment Tracking Link',
    category: 'Event',
    prefix: LinkMergeTagPrefixes.shipmentTrackingLink,
  },
} as const;

export const getLinkRelatedMergeTagKeys = (): string[] => {
  return Object.values(LinkMergeTags).map((tag) => tag.key);
};
